import React, { useEffect } from "react";
import { Link } from "gatsby";
import { useStateIfMounted } from "use-state-if-mounted";
import logo from "../assets/img/logo.png";
import LoginModal from "./LoginModal";
import MobileMenu from "./MobileMenu";
import Nav from "./Nav";
import SearchPanel from "./SearchPanel";
import useLogout from "../hooks/useLogout";
import useNavItems from "../hooks/useNavItems";
import useSession from "../hooks/useSession";

/**
 * Encabezado único del sitio.
 *
 * No hay franja superior: "Acceso clientes" aparece una sola vez, acá. Con
 * sesión iniciada ese lugar lo ocupa "Cerrar sesión" y la navegación suma
 * "Lista de precios".
 */
const Header = () => {

	const { isAuthenticated, hasPriceList } = useSession();
	const navItems = useNavItems({ hasPriceList });
	const logout = useLogout();

	const [isCompact, setIsCompact] = useStateIfMounted(false);
	const [isMenuOpen, setIsMenuOpen] = useStateIfMounted(false);
	const [isSearchOpen, setIsSearchOpen] = useStateIfMounted(false);

	useEffect(() => {
		const handleScroll = () => setIsCompact(window.scrollY > 50);
		const handleResize = () => {
			if (window.innerWidth >= 1024) setIsMenuOpen(false);
		};

		window.addEventListener("scroll", handleScroll);
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("scroll", handleScroll);
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// El scroll de fondo queda bloqueado mientras hay una capa abierta
	useEffect(() => {
		document.body.classList.toggle("overlay-open", isMenuOpen || isSearchOpen);
		return () => document.body.classList.remove("overlay-open");
	}, [isMenuOpen, isSearchOpen]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key !== "Escape") return;
			setIsMenuOpen(false);
			setIsSearchOpen(false);
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, []);

	const openSearch = () => {
		setIsMenuOpen(false);
		setIsSearchOpen(true);
	};

	const handleLogout = () => {
		setIsMenuOpen(false);
		logout();
	};

	return (
		<>
			<header className="sticky top-0 z-40 border-b border-ink/10 bg-white/95 backdrop-blur">
				<div className={ `shell flex items-center gap-6 transition-all duration-500 lg:gap-10 ${isCompact ? "min-h-[68px]" : "min-h-[72px] lg:min-h-[91px]"}` }>
					<Link to="/" aria-label="Belimport, inicio" className={ `flex-none transition-all duration-500 ${isCompact ? "w-[172px]" : "w-[184px] lg:w-[210px]"}` }>
						<img src={ logo } alt="Belimport — Importamos belleza" className="h-auto w-full object-contain" />
					</Link>

					<Nav items={ navItems } />

					<div className="ml-auto flex items-center gap-1 sm:gap-3">
						<button
							type="button"
							onClick={ openSearch }
							aria-label="Buscar"
							className="inline-flex h-11 w-11 items-center justify-center text-ink transition-colors duration-200 hover:text-brand"
						>
							<svg viewBox="0 0 24 24" aria-hidden="true" className="h-[22px] w-[22px] fill-none stroke-current" strokeWidth={1.7} strokeLinecap="round">
								<circle cx="11" cy="11" r="6.5"></circle>
								<path d="m16 16 4.2 4.2"></path>
							</svg>
						</button>

						{
							isAuthenticated
								? (
									<button
										type="button"
										onClick={ logout }
										className="hidden min-h-[44px] items-center px-[19px] text-xs font-bold text-ink-soft transition-colors duration-200 hover:text-brand lg:inline-flex"
									>
										Cerrar sesión
									</button>
								)
								: <LoginModal triggerClassName="hidden min-h-[44px] items-center bg-ink px-[19px] text-xs font-bold text-white transition-all duration-200 hover:bg-brand hover:-translate-y-px lg:inline-flex" />
						}

						<button
							type="button"
							onClick={ () => setIsMenuOpen(!isMenuOpen) }
							aria-expanded={ isMenuOpen }
							aria-controls="mobile-menu"
							aria-label={ isMenuOpen ? "Cerrar menú" : "Abrir menú" }
							className="inline-flex h-11 w-11 flex-col items-center justify-center gap-1.5 lg:hidden"
						>
							<span className={ `h-[1.5px] w-[23px] bg-ink transition-transform duration-200 ${isMenuOpen ? "translate-y-[3.75px] rotate-45" : ""}` }></span>
							<span className={ `h-[1.5px] w-[23px] bg-ink transition-transform duration-200 ${isMenuOpen ? "-translate-y-[3.75px] -rotate-45" : ""}` }></span>
						</button>
					</div>
				</div>

				<MobileMenu
					isOpen={ isMenuOpen }
					items={ navItems }
					isAuthenticated={ isAuthenticated }
					onSearchOpen={ openSearch }
					onLogout={ handleLogout }
					onNavigate={ () => setIsMenuOpen(false) }
				/>
			</header>

			<SearchPanel isOpen={ isSearchOpen } onClose={ () => setIsSearchOpen(false) } />
		</>
	);
};

export default Header;
